// client/src/pages/MediaUploadPage.js
// Standalone page for uploading media via email link

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

export default function MediaUploadPage() {
  const { uploadToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [requestDetails, setRequestDetails] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedMediaUrl, setUploadedMediaUrl] = useState('');
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    fetchRequestDetails();
  }, [uploadToken]);

  const fetchRequestDetails = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_URL}/api/media-upload/details/${uploadToken}`);
      const data = await response.json();

      if (data.success) {
        setRequestDetails(data);

        if (data.isExpired) {
          setError('This upload link has expired. Please request a new link from Digital Trent.');
        }
      } else {
        setError(data.error || 'Invalid upload link.');
      }
    } catch (err) {
      setError('Failed to load upload details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (file) => {
    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError('File size must be less than 10MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'video/mp4'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only JPG, PNG, GIF, and MP4 files are allowed');
      return;
    }

    setSelectedFile(file);
    setError('');
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const formData = new FormData();
      formData.append('file', selectedFile);

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_URL}/api/media-upload/upload/${uploadToken}`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setUploadSuccess(true);
        // Prepend API URL to mediaUrl for proper image display
        const fullImageUrl = `${API_URL}${data.mediaUrl}`;
        setUploadedMediaUrl(fullImageUrl);
      } else {
        setError(data.error || 'Upload failed. Please try again.');
      }
    } catch (err) {
      setError('An error occurred during upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
          <p className="text-gray-600">Loading upload page...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (uploadSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-check text-3xl text-green-600"></i>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Upload Successful!
          </h1>

          <p className="text-gray-600 mb-6">
            Your image has been uploaded successfully and will be attached to your social media post.
          </p>

          {uploadedMediaUrl && (
            <div className="mb-6">
              <img
                src={uploadedMediaUrl}
                alt="Uploaded"
                className="w-full rounded-lg border border-gray-200"
              />
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <i className="fas fa-info-circle mr-2"></i>
              Your image is now being synced with Digital Trent. You can close this page and return to the app.
            </p>
          </div>

          <p className="text-sm text-gray-500">
            You can now close this page and return to Digital Trent.
          </p>
        </div>
      </div>
    );
  }

  // Main upload interface
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-cloud-upload-alt text-3xl text-blue-600"></i>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Upload Your Image
          </h1>

          {requestDetails && (
            <div className="bg-gray-50 rounded-lg p-4 text-left mb-4">
              <p className="text-sm text-gray-600 mb-1">
                <strong>Post:</strong> {requestDetails.postTitle}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Requested by:</strong> {requestDetails.email}
              </p>
            </div>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              {error}
            </p>
          </div>
        )}

        {!error && requestDetails && !requestDetails.isExpired && (
          <>
            {/* Upload area */}
            <div className="mb-6">
              <label
                htmlFor="file-upload"
                className="block w-full cursor-pointer"
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? 'border-blue-500 bg-blue-50'
                    : selectedFile
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-blue-500'
                }`}>
                  <i className={`text-4xl mb-3 ${
                    selectedFile ? 'fas fa-file-image text-green-600' : 'fas fa-file-upload text-gray-400'
                  }`}></i>

                  <p className="text-sm font-medium text-gray-700 mb-1">
                    {selectedFile
                      ? selectedFile.name
                      : dragActive
                        ? 'Drop file here'
                        : 'Drag & Drop or Click to Select'
                    }
                  </p>

                  <p className="text-xs text-gray-500">
                    Supported: JPG, PNG, GIF, MP4 • Max size: 10MB
                  </p>

                  {selectedFile && (
                    <div className="mt-3">
                      <p className="text-xs text-green-600 font-medium">
                        <i className="fas fa-check-circle mr-1"></i>
                        Size: {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedFile(null);
                          setError('');
                        }}
                        className="mt-2 text-xs text-red-600 hover:text-red-700 font-medium"
                      >
                        <i className="fas fa-times mr-1"></i>
                        Remove file
                      </button>
                    </div>
                  )}
                </div>
              </label>

              <input
                id="file-upload"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,video/mp4"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => window.close()}
                className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>

              <button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className="flex-1 px-4 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-cloud-upload-alt"></i>
                    <span>Upload Image</span>
                  </>
                )}
              </button>
            </div>

            {/* Upload progress */}
            {uploading && (
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '70%' }}></div>
                </div>
                <p className="text-xs text-gray-600 text-center mt-2">
                  Uploading your file...
                </p>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Powered by <strong>Digital Trent</strong>
          </p>
        </div>
      </div>
    </div>
  );
}
